import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';

// ============================================
// COMPANY BRANDING CONFIGURATION
// ============================================
const COMPANY_CONFIG = {
    name: 'MarketRodeo',
    website: 'marketrodeo.com',
    logoUrl: 'https://marketrodeo.com/static/media/rodeoLogo.e65ef3b2ea5da4e8c0b4.png'
};

/**
 * Export dividend calculator data as PDF
 */
export const exportToPDF = (holdings, dividendData, totals, projections, settings) => {
    const doc = new jsPDF();
    
    // Manually add autoTable to doc instance
    if (!doc.autoTable && autoTable) {
        doc.autoTable = autoTable.bind(doc);
    }
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = 20;

    // ============================================
    // HEADER WITH LOGO
    // ============================================
    doc.setFontSize(20);
    doc.setFont(undefined, 'bold');
    doc.text('Dividend Portfolio Analysis', pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 10;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(100);
    const currentDate = new Date().toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
    });
    doc.text(`Generated on: ${currentDate}`, pageWidth / 2, yPos, { align: 'center' });
    
    yPos += 15;
    doc.setTextColor(0);

    // ============================================
    // CURRENT PORTFOLIO SUMMARY
    // ============================================
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Current Portfolio Summary', 14, yPos);
    yPos += 10;

    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    
    const summaryData = [
        ['Annual Income', formatCurrency(totals.totalAnnualIncome)],
        ['Quarterly Income', formatCurrency(totals.totalQuarterlyIncome)],
        ['Monthly Income', formatCurrency(totals.totalMonthlyIncome)],
        ['Portfolio Value', formatCurrency(totals.totalValue)],
        ['Average Yield', `${totals.averageYield.toFixed(2)}%`]
    ];

    autoTable(doc, {
        startY: yPos,
        head: [['Metric', 'Value']],
        body: summaryData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold' },
        styles: { fontSize: 10 },
        columnStyles: {
            0: { cellWidth: 80 },
            1: { cellWidth: 'auto', halign: 'right', fontStyle: 'bold' }
        }
    });

    yPos = doc.lastAutoTable.finalY + 15;

    // ============================================
    // HOLDINGS TABLE
    // ============================================
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text('Portfolio Holdings', 14, yPos);
    yPos += 10;

    const holdingsTableData = holdings.map(h => {
        const dividend = dividendData[h.symbol];
        return [
            h.symbol,
            h.name,
            h.shares.toString(),
            formatCurrency(h.price || 0),
            dividend?.yield ? `${dividend.yield.toFixed(2)}%` : '--',
            dividend?.amount ? formatCurrency(dividend.amount) : '--',
            dividend?.frequency || '--',
            dividend?.amount ? formatCurrency(dividend.amount * h.shares) : '--'
        ];
    });

    autoTable(doc, {
        startY: yPos,
        head: [['Symbol', 'Name', 'Shares', 'Price', 'Yield', 'Annual Div', 'Frequency', 'Annual Income']],
        body: holdingsTableData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], fontStyle: 'bold', fontSize: 8 },
        styles: { fontSize: 8 },
        columnStyles: {
            0: { cellWidth: 20 },
            1: { cellWidth: 35 },
            2: { cellWidth: 18, halign: 'right' },
            3: { cellWidth: 20, halign: 'right' },
            4: { cellWidth: 18, halign: 'right' },
            5: { cellWidth: 22, halign: 'right' },
            6: { cellWidth: 20 },
            7: { cellWidth: 25, halign: 'right', fontStyle: 'bold' }
        }
    });

    yPos = doc.lastAutoTable.finalY + 5;

    // Dividend paying info
    const dividendPayingCount = Object.keys(dividendData).filter(s => dividendData[s]?.amount > 0).length;
    doc.setFontSize(9);
    doc.setFont(undefined, 'italic');
    doc.setTextColor(100);
    doc.text(`${dividendPayingCount} of ${holdings.length} stocks pay dividends`, 14, yPos);
    doc.setTextColor(0);

    // ============================================
    // NEW PAGE FOR PROJECTIONS
    // ============================================
    if (projections && settings) {
        doc.addPage();
        yPos = 20;

        doc.setFontSize(14);
        doc.setFont(undefined, 'bold');
        doc.text('Future Projections', 14, yPos);
        yPos += 10;

        // Projection Settings
        doc.setFontSize(10);
        doc.setFont(undefined, 'normal');
        doc.text('Assumptions:', 14, yPos);
        yPos += 7;

        const assumptions = [
            `• Time Horizon: ${settings.yearsHorizon} years`,
            `• DRIP Enabled: ${settings.enableDRIP ? 'Yes' : 'No'}`,
            `• Expected Dividend Growth: ${settings.dividendGrowthRate}% per year`,
            `• Expected Share Price Growth: ${settings.sharePriceGrowthRate}% per year`
        ];

        doc.setFontSize(9);
        assumptions.forEach(assumption => {
            doc.text(assumption, 20, yPos);
            yPos += 6;
        });

        yPos += 5;

        // Projected Results Summary
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text(`Projected Results After ${settings.yearsHorizon} Years`, 14, yPos);
        yPos += 10;

        const projectionSummary = [
            ['Portfolio Value', formatCurrency(projections.finalPortfolioValue), `+${projections.totalGrowth.toFixed(1)}%`],
            [
                settings.enableDRIP ? 'Total Dividends Earned' : 'Annual Income',
                formatCurrency(settings.enableDRIP ? projections.totalDividendsEarned : projections.finalAnnualIncome),
                settings.enableDRIP ? '' : `${formatCurrency(projections.finalAnnualIncome / 12)}/month`
            ],
            ['Yield on Cost', `${projections.finalYieldOnCost.toFixed(2)}%`, `vs ${totals.averageYield.toFixed(2)}% today`],
            ['Total Gain', formatCurrency(projections.finalPortfolioValue - totals.totalValue), '']
        ];

        autoTable(doc, {
            startY: yPos,
            head: [['Metric', 'Value', 'Details']],
            body: projectionSummary,
            theme: 'grid',
            headStyles: { fillColor: [52, 152, 219], fontStyle: 'bold' },
            styles: { fontSize: 10 },
            columnStyles: {
                0: { cellWidth: 70 },
                1: { cellWidth: 50, halign: 'right', fontStyle: 'bold' },
                2: { cellWidth: 'auto', halign: 'right' }
            }
        });

        yPos = doc.lastAutoTable.finalY + 15;

        // Year-by-Year Breakdown
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.text('Year-by-Year Breakdown', 14, yPos);
        yPos += 10;

        const yearlyTableData = projections.yearlyData.map(data => {
            const row = [
                data.year.toString(),
                formatCurrency(data.portfolioValue)
            ];

            if (settings.enableDRIP) {
                row.push(formatCurrency(data.dividendsReinvested));
            } else {
                row.push(formatCurrency(data.annualDividendIncome));
            }

            row.push(formatCurrency(data.totalDividendsEarned));
            row.push(`${data.yieldOnCost.toFixed(2)}%`);

            return row;
        });

        const yearlyHeaders = [
            'Year',
            'Portfolio Value',
            settings.enableDRIP ? 'Dividends Reinvested' : 'Annual Income',
            'Total Dividends',
            'Yield on Cost'
        ];

        autoTable(doc, {
            startY: yPos,
            head: [yearlyHeaders],
            body: yearlyTableData,
            theme: 'striped',
            headStyles: { fillColor: [52, 152, 219], fontStyle: 'bold', fontSize: 9 },
            styles: { fontSize: 8 },
            columnStyles: {
                0: { cellWidth: 15, halign: 'center' },
                1: { cellWidth: 35, halign: 'right' },
                2: { cellWidth: 35, halign: 'right' },
                3: { cellWidth: 35, halign: 'right' },
                4: { cellWidth: 25, halign: 'right' }
            }
        });
    }

    // ============================================
    // FOOTER ON EVERY PAGE
    // ============================================
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        
        // Footer line
        doc.setDrawColor(200);
        doc.line(14, pageHeight - 20, pageWidth - 14, pageHeight - 20);
        
        // Footer text
        doc.setFontSize(8);
        doc.setTextColor(100);
        doc.text(`Generated by ${COMPANY_CONFIG.name} - ${COMPANY_CONFIG.website}`, 14, pageHeight - 15);
        doc.text(`Page ${i} of ${totalPages}`, pageWidth - 14, pageHeight - 15, { align: 'right' });
        
        doc.setFontSize(7);
        doc.text('Disclaimer: This analysis is for educational purposes only and not financial advice.', 
            pageWidth / 2, pageHeight - 10, { align: 'center' });
    }

    // Save PDF
    const filename = `dividend_portfolio_analysis_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(filename);
};

/**
 * Export dividend calculator as image
 */
export const exportToImage = async (elementId) => {
    const element = document.getElementById(elementId);
    if (!element) {
        console.error('Element not found');
        return;
    }

    try {
        // Capture the element as canvas with higher quality
        const canvas = await html2canvas(element, {
            scale: 2, // Back to 2 for reasonable file size
            backgroundColor: '#1a2332',
            logging: false,
            useCORS: true,
            width: element.scrollWidth, // Ensure we capture the right width
            height: element.scrollHeight
        });

        const ctx = canvas.getContext('2d');
        
        // Position branding at actual top center of captured content
        const centerX = canvas.width / 2;
        const topPadding = 60; // Increased from 30 to give more space

        // If logo URL is provided, add it at top center
        if (COMPANY_CONFIG.logoUrl) {
            try {
                const logo = new Image();
                logo.crossOrigin = 'anonymous';
                
                await new Promise((resolve, reject) => {
                    const timeout = setTimeout(() => reject(new Error('Logo load timeout')), 3000);
                    logo.onload = () => {
                        clearTimeout(timeout);
                        resolve();
                    };
                    logo.onerror = () => {
                        clearTimeout(timeout);
                        reject(new Error('Logo load failed'));
                    };
                    logo.src = COMPANY_CONFIG.logoUrl;
                });

                // Draw logo at top center
                const logoWidth = 120;
                const logoHeight = (logo.height / logo.width) * logoWidth;
                const logoX = centerX - (logoWidth / 2); // Properly center
                
                ctx.globalAlpha = 1;
                ctx.drawImage(
                    logo,
                    logoX,
                    topPadding,
                    logoWidth,
                    logoHeight
                );
                
                // Add company text below logo
                ctx.globalAlpha = 1;
                ctx.fillStyle = '#ffffff';
                ctx.font = 'bold 20px Arial';
                ctx.textAlign = 'center';
                ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                ctx.shadowBlur = 10;
                ctx.shadowOffsetX = 2;
                ctx.shadowOffsetY = 2;
                
                ctx.fillText(
                    COMPANY_CONFIG.website,
                    centerX,
                    topPadding + logoHeight + 30
                );
                
                // Reset shadow
                ctx.shadowColor = 'transparent';
            } catch (logoError) {
                console.warn('Logo loading failed, using text only:', logoError);
                addTextOnlyOverlay(ctx, centerX, topPadding);
            }
        } else {
            // No logo - just show company name and website at top center
            addTextOnlyOverlay(ctx, centerX, topPadding);
        }

        // Convert to blob and download
        canvas.toBlob((blob) => {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `dividend_calculator_${new Date().toISOString().split('T')[0]}.png`;
            a.click();
            window.URL.revokeObjectURL(url);
        });
    } catch (error) {
        console.error('Error exporting image:', error);
    }
};

// Helper function to add text-only branding as overlay
function addTextOnlyOverlay(ctx, centerX, topPadding) {
    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
    ctx.shadowBlur = 10;
    ctx.shadowOffsetX = 2;
    ctx.shadowOffsetY = 2;
    
    // Company name
    ctx.font = 'bold 24px Arial';
    ctx.fillText(
        COMPANY_CONFIG.name,
        centerX,
        topPadding + 10
    );
    
    // Website below
    ctx.font = 'bold 18px Arial';
    ctx.fillText(
        COMPANY_CONFIG.website,
        centerX,
        topPadding + 45
    );
    
    // Reset shadow
    ctx.shadowColor = 'transparent';
}

/**
 * Export dividend calculator data as CSV
 */
export const exportDividendCSV = (holdings, dividendData, projections = null, settings = null) => {
    const csvRows = [];
    const currentDate = new Date().toISOString().split('T')[0];

    // ============================================
    // HEADER WITH COMPANY INFO
    // ============================================
    csvRows.push([`${COMPANY_CONFIG.name} - Dividend Portfolio Analysis`]);
    csvRows.push([COMPANY_CONFIG.website]);
    csvRows.push(['Generated on:', currentDate]);
    csvRows.push([]); // Empty row

    // ============================================
    // CURRENT HOLDINGS
    // ============================================
    csvRows.push(['Current Portfolio Holdings']);
    csvRows.push([]); // Empty row

    // Holdings headers
    csvRows.push([
        'Symbol',
        'Name',
        'Shares',
        'Price',
        'Yield (%)',
        'Annual Dividend',
        'Frequency',
        'Annual Income'
    ]);

    // Holdings data
    holdings.forEach(h => {
        const dividend = dividendData[h.symbol];
        csvRows.push([
            h.symbol,
            h.name,
            h.shares,
            h.price || 0,
            dividend?.yield || '',
            dividend?.amount || '',
            dividend?.frequency || '',
            dividend?.amount ? (dividend.amount * h.shares).toFixed(2) : ''
        ]);
    });

    csvRows.push([]); // Empty row

    // Summary
    csvRows.push(['Portfolio Summary']);
    const totalAnnualIncome = holdings.reduce((sum, h) => {
        const dividend = dividendData[h.symbol];
        return sum + (dividend?.amount ? dividend.amount * h.shares : 0);
    }, 0);
    const totalValue = holdings.reduce((sum, h) => sum + (h.price || 0) * h.shares, 0);
    const avgYield = totalValue > 0 ? (totalAnnualIncome / totalValue) * 100 : 0;

    csvRows.push(['Total Annual Income', totalAnnualIncome.toFixed(2)]);
    csvRows.push(['Total Quarterly Income', (totalAnnualIncome / 4).toFixed(2)]);
    csvRows.push(['Total Monthly Income', (totalAnnualIncome / 12).toFixed(2)]);
    csvRows.push(['Total Portfolio Value', totalValue.toFixed(2)]);
    csvRows.push(['Average Yield (%)', avgYield.toFixed(2)]);

    // ============================================
    // PROJECTIONS (if provided)
    // ============================================
    if (projections && settings) {
        csvRows.push([]);
        csvRows.push([]);
        csvRows.push(['Future Projections']);
        csvRows.push(['Assumptions']);
        csvRows.push(['Time Horizon (years)', settings.yearsHorizon]);
        csvRows.push(['DRIP Enabled', settings.enableDRIP ? 'Yes' : 'No']);
        csvRows.push(['Expected Dividend Growth (%/year)', settings.dividendGrowthRate]);
        csvRows.push(['Expected Share Price Growth (%/year)', settings.sharePriceGrowthRate]);
        csvRows.push([]);

        csvRows.push(['Projected Results']);
        csvRows.push(['Final Portfolio Value', projections.finalPortfolioValue.toFixed(2)]);
        csvRows.push(['Total Growth (%)', projections.totalGrowth.toFixed(2)]);
        csvRows.push([
            settings.enableDRIP ? 'Total Dividends Earned' : 'Final Annual Income',
            (settings.enableDRIP ? projections.totalDividendsEarned : projections.finalAnnualIncome).toFixed(2)
        ]);
        csvRows.push(['Final Yield on Cost (%)', projections.finalYieldOnCost.toFixed(2)]);
        csvRows.push(['Total Gain', (projections.finalPortfolioValue - totalValue).toFixed(2)]);
        csvRows.push([]);

        // Year-by-year breakdown
        csvRows.push(['Year-by-Year Breakdown']);
        csvRows.push([
            'Year',
            'Portfolio Value',
            settings.enableDRIP ? 'Dividends Reinvested' : 'Annual Income',
            'Total Dividends',
            'Yield on Cost (%)'
        ]);

        projections.yearlyData.forEach(data => {
            csvRows.push([
                data.year,
                data.portfolioValue.toFixed(2),
                settings.enableDRIP ? data.dividendsReinvested.toFixed(2) : data.annualDividendIncome.toFixed(2),
                data.totalDividendsEarned.toFixed(2),
                data.yieldOnCost.toFixed(2)
            ]);
        });
    }

    // ============================================
    // CONVERT TO CSV STRING
    // ============================================
    const csvString = csvRows.map(row => 
        row.map(cell => {
            const cellStr = cell?.toString() || '';
            // Escape quotes and wrap in quotes if contains comma or quote
            if (cellStr.includes(',') || cellStr.includes('"')) {
                return `"${cellStr.replace(/"/g, '""')}"`;
            }
            return cellStr;
        }).join(',')
    ).join('\n');

    // ============================================
    // DOWNLOAD
    // ============================================
    const filename = `dividend_portfolio_${currentDate}.csv`;
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.setAttribute('href', url);
    a.setAttribute('download', filename);
    a.click();
    window.URL.revokeObjectURL(url);
};

/**
 * Helper function to format currency
 */
const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};